/* eslint-disable jsx-a11y/click-events-have-key-events */
import { Spinner } from 'components/common';
import { CreateUtilInput } from 'features/utils/api/models/CreateUtilInput';
import { stripHtml } from 'features/utils/utils/htmlText';
import { UpdateUtilInput } from 'features/utils/api/models/UpdateUtilInput';
import { UtilInfo } from 'features/utils/api/models/UtilInfo';
import {
  useCreateUtil,
  useDeleteUtil,
  usePinnedUtils,
  useTogglePinStatus,
  useUpdateUtil,
  useUtils,
} from 'features/utils/api/repositories/useUtils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import styles from './UtilsDashboard.module.scss';
import { Button } from '@progress/kendo-react-buttons';
import UtilCard from 'features/utils/components/UtilCard/UtilCard';
import UtilDialog from 'features/utils/components/UtilDialog/UtilDialog';
import { useUtilsDialog } from 'features/utils/contexts/UtilsDialogContext';
import axios from 'axios';
import { useUserProfile } from 'hooks';
import { useFilters } from 'features/utils/contexts/SearchContext';
import { DefaultEnvironment, EnvironmentType, environmentTypes } from 'features/utils/api/models/Environment';
import {
  LOCALSTORAGE_ENV_FILTER_KEY,
  LOCALSTORAGE_FREQUENTLY_USED_KEY,
  LOCALSTORAGE_CATEGORY_FILTER_KEY,
  MAX_FREQUENTLY_USED_ITEMS,
} from 'model/common/Constants';
import { DropDownListChangeEvent, DropDownList } from '@progress/kendo-react-dropdowns';
import { categories, UtilCategory } from 'features/utils/api/models/Category';

type ViewMode = 'grid' | 'list';

const UtilsDashboard: React.FC = () => {
  const { searchTerm, environmentTypeFilter, setEnvironmentTypeFilter, categoryFilter, setCategoryFilter } = useFilters();
  const envFilterDropdownItems = useMemo(() => [{ text: 'All Environments', value: null }, ...environmentTypes, DefaultEnvironment], []);
  const categoryFilterDropdownItems = useMemo(() => [{ text: 'All Categories', value: null }, ...categories], []);

  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [frequentlyUsedIds, setFrequentlyUsedIds] = useState<string[]>([]);
  const prifile = useUserProfile();

  const { data: utils = [], isLoading: isLoadingUtils, isError: isErrorUtils, error: errorUtils } = useUtils();
  const { data: pinnedUtilIds = [], isLoading: isLoadingPinnedIds, isError: isErrorPinnedIds, error: errorPinnedIds } = usePinnedUtils();

  const createUtilMutation = useCreateUtil();
  const updateUtilMutation = useUpdateUtil();
  const deleteUtilMutation = useDeleteUtil();
  const togglePinStatusMutation = useTogglePinStatus();

  const { isDialogOpen, editingUtil, openEditDialog, closeDialog } = useUtilsDialog();

  useEffect(() => {
    try {
      const storedIdsRaw = localStorage.getItem(LOCALSTORAGE_FREQUENTLY_USED_KEY);
      if (storedIdsRaw) {
        const storedIds = JSON.parse(storedIdsRaw);
        if (Array.isArray(storedIds) && storedIds.every((id) => typeof id === 'string')) {
          setFrequentlyUsedIds(storedIds);
          console.log('Loaded frequently used IDs from localStorage:', storedIds);
        } else {
          console.warn('Invalid data found in localStorage for frequently used IDs. Resetting.');
          localStorage.removeItem(LOCALSTORAGE_FREQUENTLY_USED_KEY); // Clear invalid data
        }
      }
    } catch (e) {
      console.error('Failed to load or parse frequently used IDs from localStorage:', e);
      localStorage.removeItem(LOCALSTORAGE_FREQUENTLY_USED_KEY);
    }
  }, []);

  const markUtilAsFrequentlyUsed = useCallback((utilId: string) => {
    console.log(`Marking App ID: ${utilId} as frequently used.`);
    setFrequentlyUsedIds((prevIds) => {
      // 1. Remove the ID if it already exists to move it to the front
      const filteredIds = prevIds.filter((id) => id !== utilId);
      // 2. Add the new ID to the beginning (most recent)
      const newIds = [utilId, ...filteredIds];
      // 3. Limit the array size
      const limitedIds = newIds.slice(0, MAX_FREQUENTLY_USED_ITEMS);
      // 4. Save the updated list to localStorage
      try {
        localStorage.setItem(LOCALSTORAGE_FREQUENTLY_USED_KEY, JSON.stringify(limitedIds));
        console.log('Saved updated frequently used IDs to localStorage:', limitedIds);
      } catch (e) {
        console.error('Failed to save frequently used IDs to localStorage:', e);
        toast.error('Could not update frequently used list in local storage.');
      }
      // 5. Return the new state
      return limitedIds;
    });
  }, []);

  const handlePinToggle = (id: string) => {
    const IsPinned = pinnedUtilIds.includes(id);

    togglePinStatusMutation.mutate(
      { id, isPinned: !IsPinned },
      {
        onError: (err) => {
          handleApiErrorToast(err, 'Failed to toggle pin');
        },
      },
    );
  };

  const handleEditClick = (util: UtilInfo) => {
    openEditDialog(util);
  };

  const handleCloseDialog = () => {
    closeDialog();
  };

  const handleDeleteClick = (util: UtilInfo) => {
    if (window.confirm('Are you sure you want to delete this app?')) {
      deleteUtilMutation.mutate(util.id, {
        onSuccess: () => {
          setFrequentlyUsedIds((prevIds) => {
            const updatedIds = prevIds.filter((id) => id !== util.id);
            try {
              localStorage.setItem(LOCALSTORAGE_FREQUENTLY_USED_KEY, JSON.stringify(updatedIds));
            } catch (e) {
              console.error('Failed to update localStorage after delete:', e);
            }
            return updatedIds;
          });
        },
        onError: (err) => {
          handleApiErrorToast(err, 'Failed to delete util');
        },
      });
    }
  };

  const handleSaveUtil = (data: CreateUtilInput | UpdateUtilInput) => {
    try {
      if (editingUtil) {
        updateUtilMutation.mutate(
          { id: editingUtil.id, data: data as UpdateUtilInput },
          {
            onSuccess: () => {
              toast.success(`App "${data.title || editingUtil.title}" updated successfully!`);
              handleCloseDialog();
            },
            onError: (err) => {
              handleApiErrorToast(err, 'Failed to update util');
            },
          },
        );
      } else {
        createUtilMutation.mutate(
          { data: data as CreateUtilInput },
          {
            onSuccess: (createdUtil) => {
              toast.success(`App "${createdUtil.title}" added successfully!`);
              handleCloseDialog();
            },
            onError: (err) => {
              handleApiErrorToast(err, 'Failed to add app');
            },
          },
        );
      }
    } catch (error) {
      console.error('Save operation failed:', error);
      toast.error('An unexpected error occurred during save.');
    }
  };

  function handleApiErrorToast(error: unknown, messagePrefix: string): void {
    console.error(messagePrefix, error);

    let specificErrorMessage = 'Unknown error';

    if (axios.isAxiosError(error)) {
      specificErrorMessage = error.response?.data.message;
    } else if (error instanceof Error) {
      specificErrorMessage = error.message;
    } else if (typeof error === 'string') {
      specificErrorMessage = error;
    }

    if (typeof specificErrorMessage !== 'string' || !specificErrorMessage.trim()) {
      specificErrorMessage = 'An unexpected error occurred.';
    }

    const toastMessage = `${messagePrefix}: ${specificErrorMessage}`;

    if (axios.isAxiosError(error) && error.response?.status === 400) {
      toast.warning(toastMessage);
    } else {
      toast.error(toastMessage);
    }
  }

  const applyAllFilters = useCallback(
    (
      utilsToFilter: UtilInfo[],
      currentSearchTerm: string,
      currentEnvFilter: EnvironmentType | null,
      currentCategoryFilter: UtilCategory | null,
    ): UtilInfo[] => {
      let result = [...utilsToFilter];

      const lowerSearchTerm = currentSearchTerm.toLowerCase().trim();
      if (lowerSearchTerm) {
        result = result.filter(
          (util) =>
            util.title.toLowerCase().includes(lowerSearchTerm) ||
            stripHtml(util.description).toLowerCase().includes(lowerSearchTerm),
        );
      }

      if (currentEnvFilter !== null && currentEnvFilter !== undefined) {
        result = result.filter((util) => util.environments.some((env) => env.environmentType === currentEnvFilter));
      }

      // Add category filtering logic
      if (currentCategoryFilter !== null && currentCategoryFilter !== undefined) {
        result = result.filter((util) => util.category === currentCategoryFilter);
      }
      return result;
    },
    [],
  );

  const baseFilteredUtils = useMemo(() => {
    if (!utils) return [];
    return applyAllFilters(utils, searchTerm, environmentTypeFilter, categoryFilter);
  }, [utils, searchTerm, environmentTypeFilter, categoryFilter, applyAllFilters]);

  const pinnedFilteredUtils = useMemo(() => {
    if (!baseFilteredUtils || pinnedUtilIds.length === 0) return [];
    const utilsMap = new Map(baseFilteredUtils.map((util) => [util.id, util]));
    return pinnedUtilIds.map((id) => utilsMap.get(id)).filter((util): util is UtilInfo => !!util);
  }, [baseFilteredUtils, pinnedUtilIds]);

  const frequentlyUsedFilteredUtils = useMemo(() => {
    if (!baseFilteredUtils || frequentlyUsedIds.length === 0) return [];
    const utilsMap = new Map(baseFilteredUtils.map((util) => [util.id, util]));
    return frequentlyUsedIds.map((id) => utilsMap.get(id)).filter((util): util is UtilInfo => !!util);
  }, [baseFilteredUtils, frequentlyUsedIds]);

  const allAppsFilteredUtils = baseFilteredUtils;

  if (isLoadingUtils || isLoadingPinnedIds) return <Spinner />;

  if (isErrorUtils) {
    return <div className={styles.error}>Error loading apps: {(errorUtils as Error)?.message || 'Unknown error'}</div>;
  }

  const handleEnvironmentFilterChange = (event: DropDownListChangeEvent) => {
    const { value } = event.target.value;
    setEnvironmentTypeFilter(value);
    saveEnvironmentTypeFilter(value);
  };

  const saveEnvironmentTypeFilter = (type: EnvironmentType | null) => {
    try {
      localStorage.setItem(LOCALSTORAGE_ENV_FILTER_KEY, JSON.stringify(type));
    } catch (e) {
      console.error('Failed to save environment type filter to localStorage:', e);
    }
  };

  const handleCategoryFilterChange = (event: DropDownListChangeEvent) => {
    const { value } = event.target.value;
    setCategoryFilter(value);
    saveCategoryFilter(value);
  };

  const saveCategoryFilter = (type: EnvironmentType | null) => {
    try {
      localStorage.setItem(LOCALSTORAGE_CATEGORY_FILTER_KEY, JSON.stringify(type));
    } catch (e) {
      console.error('Failed to save category type filter to localStorage:', e);
    }
  };

  const selectedEnvFilterItem = envFilterDropdownItems.find((item) => item.value === environmentTypeFilter);
  const selectedCategoryFilterItem = categoryFilterDropdownItems.find((item) => item.value === categoryFilter);

  return (
    <div className={styles.dashboard}>
      {/* Frequently Used Section */}
      <section className={styles.section}>
        <div className={styles.controls}>
          {/* Environment Filter */}
          <DropDownList
            data={envFilterDropdownItems}
            textField='text'
            dataItemKey='value'
            value={selectedEnvFilterItem}
            className={styles.filterDropdown}
            onChange={handleEnvironmentFilterChange}
            title='Filter by App Environment'
            fillMode='outline'
          />
          {/* Category Filter */}
          <DropDownList
            data={categoryFilterDropdownItems}
            textField='text'
            dataItemKey='value'
            value={selectedCategoryFilterItem}
            className={styles.filterDropdown}
            onChange={handleCategoryFilterChange}
            title='Filter by App Category'
            fillMode='outline'
          />
          <Button
            icon='grid'
            fillMode={viewMode === 'grid' ? 'solid' : 'flat'}
            themeColor='primary'
            onClick={() => setViewMode('grid')}
            title='Grid View'
          />
          <Button
            icon='grid-layout'
            fillMode={viewMode === 'list' ? 'solid' : 'flat'}
            themeColor='primary'
            onClick={() => setViewMode('list')}
            title='List View'
          />
        </div>
        {/* Pinned Apps Section */}
        {pinnedFilteredUtils.length > 0 && (
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Pinned Apps</h2>
            <div className={`${styles.serviceGrid} ${viewMode === 'list' ? styles.listView : ''}`}>
              {pinnedFilteredUtils.map((util) => (
                <UtilCard
                  key={`pinned-${util.id}`}
                  util={util}
                  viewMode={viewMode}
                  onEdit={() => handleEditClick(util)}
                  onDelete={() => handleDeleteClick(util)}
                  onUsed={() => markUtilAsFrequentlyUsed(util.id)}
                  isPinned={pinnedUtilIds.includes(util.id)}
                  onPinToggle={() => handlePinToggle(util.id)}
                  activeEnvironmentFilter={environmentTypeFilter}
                />
              ))}
            </div>
          </section>
        )}

        {frequentlyUsedFilteredUtils.length > 0 && (
          <>
            <h2 className={styles.sectionTitle}>Frequent Used</h2>
            <div className={`${styles.serviceGrid} ${viewMode === 'list' ? styles.listView : ''}`}>
              {frequentlyUsedFilteredUtils.map((util) => (
                <UtilCard
                  key={util.id}
                  util={util}
                  viewMode={viewMode}
                  onEdit={() => handleEditClick(util)}
                  onDelete={() => handleDeleteClick(util)}
                  onUsed={() => {}}
                  isPinned={pinnedUtilIds.includes(util.id)}
                  onPinToggle={() => handlePinToggle(util.id)}
                  activeEnvironmentFilter={environmentTypeFilter}
                />
              ))}
            </div>
          </>
        )}
      </section>

      {/* All Apps Section */}
      <section className={styles.section}>
        <div className={styles.serviceHeader}>
          <h2 className={styles.sectionTitle}>Apps</h2>
        </div>
        <div className={`${styles.serviceGrid} ${viewMode === 'list' ? styles.listView : ''}`}>
          {allAppsFilteredUtils.map((util) => (
            <UtilCard
              key={util.id}
              util={util}
              viewMode={viewMode}
              onEdit={() => handleEditClick(util)}
              onDelete={() => handleDeleteClick(util)}
              onUsed={() => markUtilAsFrequentlyUsed(util.id)}
              isPinned={pinnedUtilIds.includes(util.id)}
              onPinToggle={() => handlePinToggle(util.id)}
              activeEnvironmentFilter={environmentTypeFilter}
            />
          ))}
        </div>

        {allAppsFilteredUtils.length === 0 &&
          utils.length > 0 &&
          (searchTerm || environmentTypeFilter !== null || categoryFilter !== null) && (
            <p className={styles.noResults}>No services found matching your criteria.</p>
          )}

        {utils.length === 0 && !isLoadingUtils && (
          <p className={styles.noResults}>
            No services configured yet.{' '}
            {prifile?.isAdmin && (
              <span>
                Click the <b>Add App</b> button to add one.
              </span>
            )}
          </p>
        )}
      </section>

      {/* Dialog */}
      <UtilDialog
        visible={isDialogOpen}
        onClose={handleCloseDialog}
        onSave={handleSaveUtil}
        initialData={editingUtil}
        isSaving={createUtilMutation.isPending || updateUtilMutation.isPending}
      />
    </div>
  );
};

export default UtilsDashboard;
